import React, { useState } from 'react';
import {
  Monitor,
  Smartphone,
  Apple,
  Shield,
  Trash2,
  Upload,
  Lock,
  FileText,
  CheckCircle,
  Laptop,
  Terminal,
  HardDrive,
  Wifi,
  Key,
  UserX,
  Settings
} from 'lucide-react';

const iconLookup = {
  terminal: Terminal,
  file: FileText,
  shield: Shield
};

const PolicyDeviceCards = ({
  scripts = [],
  onSelectScript,
  onCreatePolicy,
  customPolicies = {},
  initialSelectedDeviceType = 'windows',
  allowedPlatforms = null,
}) => {
  const [selectedDeviceType, setSelectedDeviceType] = useState(initialSelectedDeviceType);

  // Organize scripts by device type
  const organizeByDeviceType = () => {
    // Windows scripts: All PowerShell scripts and policies
    const windowsScripts = scripts.filter(s => 
      s.lang === 'PowerShell' || 
      s.title.toLowerCase().includes('windows') ||
      s.category === 'cat-policies'
    ).map(s => ({
      ...s,
      deviceType: 'windows',
      type: 'script'
    }));

    const androidScripts = [
      {
        id: 'android-wipe',
        title: 'Wipe Device',
        description: 'Remotely wipe all data from Android device. This action is irreversible.',
        icon: Trash2,
        color: 'text-red-500',
        risk: 'Critical',
        deviceType: 'android',
        type: 'command'
      },
      {
        id: 'android-lock',
        title: 'Lock Device',
        description: 'Remotely lock the Android device screen.',
        icon: Lock,
        color: 'text-yellow-500',
        risk: 'Medium',
        deviceType: 'android',
        type: 'command'
      },
      {
        id: 'android-create-profile',
        title: 'Create Work Profile',
        description: 'Create and configure Android work profile for device management.',
        icon: Upload,
        color: 'text-blue-500',
        risk: 'Low',
        deviceType: 'android',
        type: 'command'
      },
      {
        id: 'android-push-profile',
        title: 'Push Policy Profile',
        description: 'Push policy configuration to Android device work profile.',
        icon: Upload,
        color: 'text-green-500',
        risk: 'Low',
        deviceType: 'android',
        type: 'command'
      },
      {
        id: 'android-sync-policy',
        title: 'Sync Policy',
        description: 'Force sync policy from server to Android device.',
        icon: CheckCircle,
        color: 'text-blue-500',
        risk: 'Low',
        deviceType: 'android',
        type: 'command'
      }
    ];

    // macOS policies (using shell scripts)
    const macosScripts = [
      {
        id: 'macos-enable-firewall',
        title: 'Enable Firewall',
        description: 'Enable macOS built-in firewall for enhanced security.',
        icon: Shield,
        color: 'text-green-500',
        risk: 'High',
        deviceType: 'macos',
        type: 'script',
        lang: 'Shell'
      },
      {
        id: 'macos-disable-guest',
        title: 'Disable Guest Account',
        description: 'Disable the macOS Guest account to prevent unauthorized access.',
        icon: UserX,
        color: 'text-orange-500',
        risk: 'Low',
        deviceType: 'macos',
        type: 'script',
        lang: 'Shell'
      },
      {
        id: 'macos-require-password',
        title: 'Require Password on Wake',
        description: 'Require password immediately after sleep or screen saver begins.',
        icon: Key,
        color: 'text-blue-500',
        risk: 'Medium',
        deviceType: 'macos',
        type: 'script',
        lang: 'Shell'
      },
      {
        id: 'macos-disable-sip',
        title: 'System Integrity Protection',
        description: 'Configure System Integrity Protection (SIP) settings.',
        icon: Shield,
        color: 'text-red-500',
        risk: 'High',
        deviceType: 'macos',
        type: 'script',
        lang: 'Shell'
      },
      {
        id: 'macos-encrypt-disk',
        title: 'Enable FileVault Encryption',
        description: 'Enable full disk encryption using FileVault on macOS.',
        icon: HardDrive,
        color: 'text-purple-500',
        risk: 'High',
        deviceType: 'macos',
        type: 'script',
        lang: 'Shell'
      },
      {
        id: 'macos-wifi-config',
        title: 'Configure Wi-Fi Settings',
        description: 'Configure Wi-Fi network settings and restrictions.',
        icon: Wifi,
        color: 'text-blue-500',
        risk: 'Low',
        deviceType: 'macos',
        type: 'script',
        lang: 'Shell'
      }
    ];

    // Linux policies (using bash scripts)
    const linuxScripts = [
      {
        id: 'linux-enable-ufw',
        title: 'Enable UFW Firewall',
        description: 'Enable and configure Uncomplicated Firewall (UFW) on Linux systems.',
        icon: Shield,
        color: 'text-green-500',
        risk: 'High',
        deviceType: 'linux',
        type: 'script',
        lang: 'Bash'
      },
      {
        id: 'linux-disable-root-login',
        title: 'Disable Root SSH Login',
        description: 'Disable direct root login via SSH for enhanced security.',
        icon: UserX,
        color: 'text-red-500',
        risk: 'High',
        deviceType: 'linux',
        type: 'script',
        lang: 'Bash'
      },
      {
        id: 'linux-update-packages',
        title: 'Update System Packages',
        description: 'Update all system packages to latest security patches.',
        icon: CheckCircle,
        color: 'text-blue-500',
        risk: 'Medium',
        deviceType: 'linux',
        type: 'script',
        lang: 'Bash'
      },
      {
        id: 'linux-configure-sudo',
        title: 'Configure Sudo Access',
        description: 'Configure sudo permissions and password requirements.',
        icon: Key,
        color: 'text-yellow-500',
        risk: 'Medium',
        deviceType: 'linux',
        type: 'script',
        lang: 'Bash'
      },
      {
        id: 'linux-enable-selinux',
        title: 'Enable SELinux',
        description: 'Enable and configure SELinux (Security-Enhanced Linux) on supported distributions.',
        icon: Shield,
        color: 'text-orange-500',
        risk: 'High',
        deviceType: 'linux',
        type: 'script',
        lang: 'Bash'
      },
      {
        id: 'linux-disable-unused-services',
        title: 'Disable Unused Services',
        description: 'Disable unnecessary system services to reduce attack surface.',
        icon: Settings,
        color: 'text-gray-500',
        risk: 'Medium',
        deviceType: 'linux',
        type: 'script',
        lang: 'Bash'
      },
      {
        id: 'linux-configure-iptables',
        title: 'Configure iptables Rules',
        description: 'Configure iptables firewall rules for network security.',
        icon: Shield,
        color: 'text-blue-500',
        risk: 'High',
        deviceType: 'linux',
        type: 'script',
        lang: 'Bash'
      }
    ];

    // iOS policies (using MDM commands)
    const iosScripts = [
      {
        id: 'ios-wipe',
        title: 'Erase Device',
        description: 'Remotely erase all data from iOS device. This action is irreversible.',
        icon: Trash2,
        color: 'text-red-500',
        risk: 'Critical',
        deviceType: 'ios',
        type: 'command'
      },
      {
        id: 'ios-lock',
        title: 'Lock Device',
        description: 'Remotely lock the iOS device screen.',
        icon: Lock,
        color: 'text-yellow-500',
        risk: 'Medium',
        deviceType: 'ios',
        type: 'command'
      },
      {
        id: 'ios-install-profile',
        title: 'Install Configuration Profile',
        description: 'Install MDM configuration profile on iOS device.',
        icon: Upload,
        color: 'text-blue-500',
        risk: 'Low',
        deviceType: 'ios',
        type: 'command'
      },
      {
        id: 'ios-remove-profile',
        title: 'Remove Configuration Profile',
        description: 'Remove MDM configuration profile from iOS device.',
        icon: Trash2,
        color: 'text-orange-500',
        risk: 'Medium',
        deviceType: 'ios',
        type: 'command'
      },
      {
        id: 'ios-device-info',
        title: 'Get Device Information',
        description: 'Retrieve detailed device information from iOS device.',
        icon: FileText,
        color: 'text-green-500',
        risk: 'Low',
        deviceType: 'ios',
        type: 'command'
      },
      {
        id: 'ios-restrictions',
        title: 'Apply Restrictions',
        description: 'Apply security restrictions and policies to iOS device.',
        icon: Shield,
        color: 'text-purple-500',
        risk: 'Medium',
        deviceType: 'ios',
        type: 'command'
      }
    ];

    const base = {
      windows: windowsScripts,
      android: androidScripts,
      macos: macosScripts,
      linux: linuxScripts,
      ios: iosScripts
    };

    const attachCustom = (collection) => {
      const cloned = { ...collection };
      if (customPolicies) {
        Object.entries(customPolicies).forEach(([deviceType, policies]) => {
          if (!deviceType || !Array.isArray(policies)) return;
          const normalized = policies.map((policy) => ({
            ...policy,
            icon: iconLookup[policy.iconName] || policy.icon || Shield,
            lang: policy.lang || policy.commandType || 'Custom',
            deviceType,
            color: policy.color || 'text-indigo-500',
            type: policy.type || 'script',
            risk: policy.risk || 'Medium',
            category: policy.category || 'custom'
          }));
          cloned[deviceType] = [...(cloned[deviceType] || []), ...normalized];
        });
      }
      return cloned;
    };

    return attachCustom(base);
  };

  const allDeviceTypes = [
    { id: 'windows', name: 'Windows', icon: Monitor, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', description: 'USB, app install, local users, security' },
    { id: 'macos', name: 'macOS', icon: Laptop, color: 'text-gray-600', bgColor: 'bg-slate-50', borderColor: 'border-slate-200', description: 'Firewall, guest account, encryption' },
    { id: 'linux', name: 'Linux', icon: Terminal, color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200', description: 'UFW, sudo, packages, services' },
    { id: 'android', name: 'Android', icon: Smartphone, color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200', description: 'Work profile and MDM actions' },
    { id: 'ios', name: 'iOS', icon: Apple, color: 'text-gray-600', bgColor: 'bg-gray-50', borderColor: 'border-gray-200', description: 'MDM restrictions and info' },
  ];
  const deviceTypes = Array.isArray(allowedPlatforms) && allowedPlatforms.length
    ? allDeviceTypes.filter((d) => allowedPlatforms.includes(d.id))
    : allDeviceTypes;

  const organizedScripts = organizeByDeviceType();
  const selectedScripts = selectedDeviceType ? organizedScripts[selectedDeviceType] : [];
  const showOsCards = Array.isArray(allowedPlatforms) && allowedPlatforms.length > 0;

  const getRiskBadge = (risk) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-red-100 text-red-800',
      'Critical': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[risk] || 'bg-gray-100 text-gray-800'}`}>
        {risk}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {showOsCards && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {deviceTypes.map((deviceType) => {
            const Icon = deviceType.icon;
            const count = (organizedScripts[deviceType.id] || []).length;
            const isSelected = selectedDeviceType === deviceType.id;
            return (
              <div
                key={deviceType.id}
                onClick={() => setSelectedDeviceType(deviceType.id)}
                className={`${deviceType.bgColor} ${deviceType.borderColor} border-2 rounded-lg p-5 cursor-pointer transition-all hover:shadow-lg ${
                  isSelected ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`${deviceType.color} p-3 rounded-lg bg-white`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-700">
                    {count}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{deviceType.name}</h3>
                <p className="text-sm text-gray-600">{deviceType.description}</p>
              </div>
            );
          })}
        </div>
      )}

      {selectedDeviceType && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {(() => {
                const deviceType = deviceTypes.find(d => d.id === selectedDeviceType);
                const Icon = deviceType?.icon || Settings;
                return (
                  <div className="flex items-center">
                    <Icon className={`w-5 h-5 mr-2 ${deviceType?.color || 'text-gray-600'}`} />
                    <h3 className="text-lg font-semibold">
                      {deviceType?.name} Policies ({selectedScripts.length})
                    </h3>
                  </div>
                );
              })()}
              {!showOsCards && (
                <div className="flex flex-wrap gap-1" role="tablist" aria-label="Platform">
                  {deviceTypes.map((deviceType) => (
                    <button
                      key={deviceType.id}
                      type="button"
                      role="tab"
                      aria-selected={selectedDeviceType === deviceType.id}
                      onClick={() => setSelectedDeviceType(deviceType.id)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        selectedDeviceType === deviceType.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {deviceType.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => onCreatePolicy && onCreatePolicy(selectedDeviceType)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <FileText className="w-4 h-4" />
              Create Policy
            </button>
          </div>

          <div className="p-4">
            {selectedScripts.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-4">No policies for {deviceTypes.find(d => d.id === selectedDeviceType)?.name} devices</p>
                <button
                  onClick={() => onCreatePolicy && onCreatePolicy(selectedDeviceType)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Create First Policy
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedScripts.map((script) => {
                  const Icon = script.icon || Shield;
                  return (
                    <div
                      key={script.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => onSelectScript && onSelectScript(script)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className={`${script.color || 'text-blue-500'} p-2 rounded-lg bg-gray-50`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        {getRiskBadge(script.risk)}
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{script.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{script.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {script.lang || script.type || 'Policy'}
                        </span>
                        <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          View Details →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyDeviceCards;

